import { TextField } from '@mui/material'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Dialog from '../common/dialog'
import { requestAccessToken } from '../../api/auth.api'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { CustomerCompanyFunding, FormError } from '../../types'
import { AuthContext, NotificationsContext } from '../../contexts'
import { rejectCustomerCompanyFunding } from '../../api/customers.api'

export const RejectCustomerCompanyFundingModal = ({
  selectedCustomerCompanyFunding,
  handleSetModalOpenState,
  handleCloseFundingAllocationModal
}: {
  selectedCustomerCompanyFunding?: CustomerCompanyFunding
  handleSetModalOpenState: (openState: boolean) => void
  handleCloseFundingAllocationModal: () => void
}) => {
  const navigate = useNavigate()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-company-details-management', 'common'])

  const [errors, setErrors] = useState<FormError>({})
  const [dialogIsLoading, setDialogIsLoading] = useState<boolean>(false)
  const [customerCompanyFundingRejectReason, setCustomerCompanyFundingRejectReason] = useState('')

  const handleRejectCustomerCompanyFunding = async () => {
    setErrors({})

    if (!customerCompanyFundingRejectReason) {
      setErrors({
        customerCompanyFundingRejectReason: t('rejectFundingModal.error')
      })
      return
    }

    try {
      setDialogIsLoading(true)

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await rejectCustomerCompanyFunding(String(selectedCustomerCompanyFunding?._id), { customerCompanyFundingRejectReason }, accessTokenData.data.accessToken)
      handleSetModalOpenState(false)
      handleCloseFundingAllocationModal()
      updateNewNotificationStatus?.(true, 'reject-funding-allocation-success')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      actionButtonWarningStyle
      dialogIsLoading={dialogIsLoading}
      dialogTitle={t('rejectFundingModal.title')}
      handleDialogAction={() => handleRejectCustomerCompanyFunding()}
      handleCloseOptionAction={() => {
        handleSetModalOpenState(false)
      }}
      actionButtonText={t('fundingDetailsModal.actionButtonRejectText')}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
    >
      <div className="customer-company-funding-rejection-main-instruction">{t('rejectFundingModal.rejectionMainInstruction')}</div>
      <TextField
        rows={4}
        multiline
        variant="standard"
        sx={textFieldWhiteBg}
        value={customerCompanyFundingRejectReason}
        placeholder={t('rejectFundingModal.placeholder')}
        error={!!errors.customerCompanyFundingRejectReason}
        helperText={errors.customerCompanyFundingRejectReason}
        onChange={(event) => setCustomerCompanyFundingRejectReason(event.target.value)}
      />
      {errors?.generalError && (
        <div
          className="error-message"
          style={{ margin: '20px 0px 0px' }}
        >
          {errors?.generalError}
        </div>
      )}
    </Dialog>
  )
}
